import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Crown, Check, X, Zap, Shield, Users, Clock, Image, 
  BarChart3, Headphones, ChevronRight, Sparkles, Loader2,
  Package, Lock, Unlock, TrendingUp, Download, RefreshCw,
  ChevronDown, ChevronUp, CreditCard, Gift, Activity,
  Layers, FileText, Bell, Star, ArrowRight, Info
} from 'lucide-react';
import { subscriptionAPI, paymentAPI } from '../../api';
import toast from 'react-hot-toast';
import { useRazorpay } from 'react-razorpay';

// Feature comparison data - comprehensive features list
const FEATURE_COMPARISON = [
  {
    category: 'Usage Pricing (₹ per call)',
    features: [
      { name: 'Virtual Try-On', starter: '₹24', growth: '₹22', professional: '₹20', enterprise: 'Custom' },
      { name: 'Moments API', starter: '₹10', growth: '₹9', professional: '₹8', enterprise: 'Custom' },
      { name: 'Size Recommendation', starter: '₹3', growth: '₹2', professional: '₹1.5', enterprise: 'Custom' },
      { name: 'Repeat Try-Ons (Cache)', starter: 'Free', growth: 'Free', professional: 'Free', enterprise: 'Free' },
      { name: 'Included Usage Balance/mo', starter: '₹500', growth: '₹1,500', professional: '₹3,000', enterprise: 'Custom' },
    ]
  },
  {
    category: 'Processing & Performance',
    features: [
      { name: 'API Processing Queue', starter: 'Shared', growth: 'Priority', professional: 'Priority', enterprise: 'Dedicated' },
      { name: 'Free Re-runs (failed calls)', starter: false, growth: true, professional: true, enterprise: true },
      { name: '99.9% Uptime SLA', starter: false, growth: false, professional: false, enterprise: true },
    ]
  },
  {
    category: 'Inventory & SKUs',
    features: [
      { name: 'SKU Limit', starter: '10', growth: '25', professional: '100', enterprise: 'Unlimited' },
      { name: 'Inventory Management', starter: true, growth: true, professional: true, enterprise: true },
      { name: 'Multiple Images per SKU', starter: false, growth: true, professional: true, enterprise: true },
      { name: 'Pre-loaded SKU Try-On Links', starter: false, growth: true, professional: true, enterprise: true },
      { name: 'Rate Limits per SKU', starter: false, growth: true, professional: true, enterprise: true },
    ]
  },
  {
    category: 'Analytics & Reporting',
    features: [
      { name: 'Basic Usage Dashboard', starter: true, growth: true, professional: true, enterprise: true },
      { name: 'Advanced Analytics Dashboard', starter: false, growth: false, professional: true, enterprise: true },
      { name: 'SKU-level Performance', starter: false, growth: false, professional: true, enterprise: true },
      { name: 'Exportable Reports', starter: false, growth: false, professional: true, enterprise: true },
      { name: 'Log Retention', starter: '30 days', growth: '90 days', professional: '180 days', enterprise: '365 days' },
    ]
  },
  {
    category: 'Team & Collaboration',
    features: [
      { name: 'Team Members', starter: '1', growth: '3', professional: '5', enterprise: 'Unlimited' },
      { name: 'Role-based Access', starter: false, growth: true, professional: true, enterprise: true },
    ]
  },
  {
    category: 'Branding & Output',
    features: [
      { name: 'Custom Watermark Control', starter: false, growth: true, professional: true, enterprise: true },
      { name: 'Watermark-free Output', starter: false, growth: true, professional: true, enterprise: true },
      { name: 'Shareable Try-On Links', starter: true, growth: true, professional: true, enterprise: true },
    ]
  },
  {
    category: 'Support',
    features: [
      { name: 'Email Support', starter: true, growth: true, professional: true, enterprise: true },
      { name: 'Priority Email Support', starter: false, growth: true, professional: true, enterprise: true },
      { name: 'Dedicated Account Manager', starter: false, growth: false, professional: false, enterprise: true },
      { name: 'Custom Onboarding', starter: false, growth: false, professional: false, enterprise: true },
    ]
  }
];

// Helper function to get plan order
const getPlanOrder = (planId) => {
  const order = { trial: -1, starter: 0, basic: 0, growth: 1, professional: 2, enterprise: 3 };
  return order[planId?.toLowerCase()] ?? 0;
};

// Plan Icon Component
const PlanIcon = ({ planId, className = "w-6 h-6" }) => {
  switch (planId?.toLowerCase()) {
    case 'trial':
      return <Gift className={className} />;
    case 'starter':
    case 'basic':
      return <Zap className={className} />;
    case 'growth':
      return <TrendingUp className={className} />;
    case 'professional':
      return <BarChart3 className={className} />;
    case 'enterprise':
      return <Crown className={className} />;
    default:
      return <Zap className={className} />;
  }
};

// Feature Checkmark or Value
const FeatureValue = ({ value, highlight = false }) => {
  if (value === true) {
    return <Check className={`w-4 h-4 sm:w-5 sm:h-5 mx-auto ${highlight ? 'text-blue-600' : 'text-emerald-500'}`} />;
  }
  if (value === false) {
    return <X className="w-4 h-4 sm:w-5 sm:h-5 text-slate-300 mx-auto" />;
  }
  return (
    <span className={`text-[9px] sm:text-sm font-medium ${highlight ? 'text-blue-600' : 'text-slate-700'}`}>
      {value}
    </span>
  );
};

// Plan Benefits Summary - Key value props
const PlanBenefits = ({ plan }) => {
  const benefits = {
    trial: [
      { icon: Gift, text: '₹500 free usage balance' },
      { icon: Zap, text: 'Virtual Try-On: ₹24/call' },
      { icon: Package, text: 'Up to 5 product SKUs' },
    ],
    starter: [
      { icon: Gift, text: '₹500 usage balance/month included' },
      { icon: Zap, text: 'Virtual Try-On: ₹24/call' },
      { icon: Package, text: 'Up to 10 product SKUs' },
      { icon: Headphones, text: 'Email & community support' },
    ],
    growth: [
      { icon: Gift, text: '₹1,500 usage balance/month included' },
      { icon: Activity, text: 'Priority processing queue' },
      { icon: Users, text: 'Up to 3 team members' },
      { icon: Layers, text: '25 SKUs with multiple images' },
    ],
    professional: [
      { icon: Gift, text: '₹3,000 usage balance/month included' },
      { icon: BarChart3, text: 'Advanced analytics & exports' },
      { icon: Users, text: 'Up to 5 team members' },
      { icon: Layers, text: '100 SKUs with full features' },
    ],
    enterprise: [
      { icon: Shield, text: 'Dedicated processing lane' },
      { icon: Star, text: '99.9% uptime SLA guarantee' },
      { icon: Headphones, text: 'Dedicated account manager' },
      { icon: Sparkles, text: 'Custom limits & integrations' },
    ]
  };
  
  return (
    <div className="space-y-2">
      {(benefits[plan.id?.toLowerCase()] || benefits.starter).map((benefit, i) => (
        <div key={i} className="flex items-center gap-2 text-sm text-slate-600">
          <benefit.icon className="w-4 h-4 text-blue-500 flex-shrink-0" />
          <span>{benefit.text}</span>
        </div>
      ))}
    </div>
  );
};

const Subscription = () => {
  const { Razorpay } = useRazorpay();
  const [plans, setPlans] = useState([]);
  const [currentPlan, setCurrentPlan] = useState('starter');
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [upgrading, setUpgrading] = useState(null); // Track which plan is being upgraded
  const [isIntroPeriod, setIsIntroPeriod] = useState(true);
  const [introEndDate, setIntroEndDate] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedPlanForPayment, setSelectedPlanForPayment] = useState(null);
  const [paymentStep, setPaymentStep] = useState('confirm'); // confirm, processing, success
  const [subscriptionLinks, setSubscriptionLinks] = useState({});

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [plansRes, currentRes, linksRes] = await Promise.all([
        subscriptionAPI.getPlans(),
        subscriptionAPI.getCurrent(),
        paymentAPI.getSubscriptionLinks().catch(() => ({ data: { links: {} } }))
      ]);
      
      setSubscriptionLinks(linksRes.data?.links || {});
      
      const mappedPlans = (plansRes.data.plans || []).map(plan => ({
        id: plan.plan_id,
        name: plan.name,
        tagline: plan.tagline,
        description: plan.description,
        price_monthly: plan.price_monthly || 0,
        price_monthly_intro: plan.price_monthly_intro,
        effective_price: plan.effective_price,
        monthly_credits: plan.monthly_credits || 0,
        monthly_credits_intro: plan.monthly_credits_intro,
        effective_credits: plan.effective_credits,
        tryon_cost: plan.tryon_cost,
        moments_cost: plan.moments_cost,
        max_skus: plan.max_skus,
        max_team_members: plan.max_team_members,
        features_list: plan.features_list || [],
        limitations: plan.limitations || [],
        support_level: plan.support_level,
        popular: plan.popular || false,
        cta_text: plan.cta_text
      }));
      
      setPlans(mappedPlans);
      setCurrentPlan((currentRes.data.current_plan || 'starter').toLowerCase());
      setSubscription(currentRes.data);
      setIsIntroPeriod(plansRes.data.is_intro_period);
      setIntroEndDate(plansRes.data.intro_end_date);
    } catch (error) {
      console.error('Failed to load subscription data:', error);
      toast.error('Failed to load subscription data');
    } finally {
      setLoading(false);
    }
  };

  const openPaymentModal = (plan) => {
    if (plan.id === 'enterprise') {
      window.location.href = '/contact';
      return;
    }
    
    // For Growth and Professional, use hosted payment links
    const planId = plan.id?.toLowerCase();
    const hostedLink = subscriptionLinks[planId];
    
    if (hostedLink) {
      // Open hosted payment link in new tab
      window.open(hostedLink, '_blank');
      toast.success('Opening payment page. Your plan will activate after payment.', { duration: 5000 });
      return;
    }
    
    // Fallback to in-app payment if no hosted link configured
    setSelectedPlanForPayment(plan);
    setPaymentStep('confirm');
    setShowPaymentModal(true);
  };

  const handleUpgrade = async () => {
    const plan = selectedPlanForPayment;
    if (!plan || plan.effective_price <= 0) return;
    
    setUpgrading(plan.id);
    setPaymentStep('processing');
    
    try {
      const orderRes = await paymentAPI.createOrder({
        order_type: 'subscription',
        plan: plan.id,
        plan_id: plan.id,
        amount: plan.effective_price,  // Amount in rupees, backend handles paise conversion
        billing_cycle: 'monthly'
      });

      const { order_id, key_id, mock, amount: razorpayAmount } = orderRes.data;

      if (mock) {
        await new Promise(resolve => setTimeout(resolve, 1500));
        await paymentAPI.verifyPayment({
          razorpay_payment_id: 'mock_' + Date.now(),
          razorpay_order_id: order_id,
          razorpay_signature: 'mock_signature',
          order_type: 'subscription'
        });
        setPaymentStep('success');
        setTimeout(() => {
          setShowPaymentModal(false);
          fetchData();
          toast.success(`Successfully upgraded to ${plan.name}!`);
        }, 2000);
        return;
      }

      const options = {
        key: key_id,
        amount: razorpayAmount,  // Use amount from backend (already in paise)
        currency: 'INR',
        name: 'NanoKart.ai',
        description: `${plan.name} Plan - Monthly Subscription`,
        order_id: order_id,
        handler: async (response) => {
          try {
            setPaymentStep('processing');
            await paymentAPI.verifyPayment({
              ...response,
              order_type: 'subscription'
            });
            setPaymentStep('success');
            setTimeout(() => {
              setShowPaymentModal(false);
              fetchData();
              toast.success(`Successfully upgraded to ${plan.name}!`);
            }, 2000);
          } catch (err) {
            toast.error(err.response?.data?.detail || 'Payment verification failed');
            setPaymentStep('confirm');
          }
        },
        modal: {
          ondismiss: () => {
            setPaymentStep('confirm');
            setUpgrading(null);
          }
        },
        theme: { color: '#2563eb' }
      };

      setPaymentStep('confirm');
      const rzp = new Razorpay(options);
      rzp.open();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate upgrade');
      setPaymentStep('confirm');
    } finally {
      setUpgrading(null);
    }
  };

  const getSupportLabel = (level) => {
    switch (level) {
      case 'email_community': return 'Email & Community';
      case 'priority_email': return 'Priority Email';
      case 'priority': return 'Priority Support';
      case 'dedicated': return 'Dedicated Support';
      default: return level;
    }
  };

  const calculateGST = (amount) => Math.round(amount * 0.18);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  const normalizedCurrentPlan = currentPlan === 'basic' ? 'starter' : currentPlan;

  return (
    <div className="space-y-5 sm:space-y-8 w-full max-w-7xl mx-auto overflow-x-hidden" data-testid="subscription-page">
      {/* Current Subscription Status */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl sm:rounded-2xl p-2.5 sm:p-6 text-white overflow-hidden"
      >
        <div className="flex flex-col gap-2.5 sm:gap-6">
          <div>
            <p className="text-blue-100 text-[10px] sm:text-sm mb-0.5 sm:mb-1">Current Plan</p>
            <h2 className="text-base sm:text-2xl font-bold flex items-center gap-1.5 sm:gap-2">
              <PlanIcon planId={currentPlan} className="w-4 h-4 sm:w-6 sm:h-6" />
              {plans.find(p => p.id?.toLowerCase() === normalizedCurrentPlan)?.name || 'Starter'} Plan
            </h2>
            {subscription?.subscription_end_at && normalizedCurrentPlan !== 'starter' && (
              <p className="text-blue-200 text-[10px] sm:text-sm mt-0.5 sm:mt-1">
                {subscription.subscription_status === 'active' ? 'Renews' : 'Expired'}: {new Date(subscription.subscription_end_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
              </p>
            )}
            {subscription?.has_custom_pricing && (
              <span className="inline-block mt-1.5 sm:mt-2 px-2 py-0.5 sm:py-1 bg-amber-400 text-amber-900 text-[10px] sm:text-xs font-semibold rounded">
                Custom Pricing Applied
              </span>
            )}
          </div>
          
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5 sm:gap-4">
            <div className="bg-white/10 rounded-lg p-2 sm:p-3 text-center min-w-0">
              <p className="text-blue-100 text-[10px] sm:text-xs truncate">Usage Balance</p>
              <p className="text-base sm:text-2xl font-bold">₹{subscription?.usage_balance?.toLocaleString() || subscription?.credits_remaining?.toLocaleString() || 0}</p>
            </div>
            <div className="bg-white/10 rounded-lg p-2 sm:p-3 text-center min-w-0">
              <p className="text-blue-100 text-[10px] sm:text-xs truncate"><span className="sm:hidden">VTO</span><span className="hidden sm:inline">VTO Cost</span></p>
              <p className="text-base sm:text-2xl font-bold">₹{subscription?.tryon_cost || 24}</p>
            </div>
            <div className="bg-white/10 rounded-lg p-2 sm:p-3 text-center min-w-0">
              <p className="text-blue-100 text-[10px] sm:text-xs truncate">Moments</p>
              <p className="text-base sm:text-2xl font-bold">₹{subscription?.moments_cost || 10}</p>
            </div>
            <div className="bg-white/10 rounded-lg p-2 sm:p-3 text-center min-w-0">
              <p className="text-blue-100 text-[10px] sm:text-xs truncate"><span className="sm:hidden">SKUs</span><span className="hidden sm:inline">SKU Limit</span></p>
              <p className="text-base sm:text-2xl font-bold">
                {subscription?.max_skus === -1 ? '∞' : subscription?.max_skus || 10}
              </p>
            </div>
          </div>
        </div>
        
        {/* Feature Access Summary */}
        <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-white/20 flex flex-wrap gap-1.5 sm:gap-2">
          <FeatureBadge 
            enabled={true} 
            label="Inventory" 
            icon={<Package className="w-3 h-3" />}
          />
          <FeatureBadge 
            enabled={subscription?.has_analytics || normalizedCurrentPlan !== 'starter'} 
            label="Analytics" 
            icon={<BarChart3 className="w-3 h-3" />}
          />
          <FeatureBadge 
            enabled={subscription?.has_priority_queue || normalizedCurrentPlan !== 'starter'} 
            label="Priority" 
            icon={<Zap className="w-3 h-3" />}
          />
          <FeatureBadge 
            enabled={(subscription?.max_team_members || 0) > 1} 
            label={`${subscription?.max_team_members || 1} Team`} 
            icon={<Users className="w-3 h-3" />}
          />
        </div>
      </motion.div>

      {/* Intro Offer Banner */}
      {isIntroPeriod && (
        <motion.div 
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-3 sm:p-4 flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-3"
        >
          <div className="p-2 bg-amber-100 rounded-lg flex-shrink-0">
            <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 text-amber-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-amber-800 text-sm sm:text-base">🎉 Introductory Offer Active!</p>
            <p className="text-xs sm:text-sm text-amber-700">
              Special pricing until {introEndDate ? new Date(introEndDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : 'Jan 31, 2026'}. 
              Lock in these rates now!
            </p>
          </div>
        </motion.div>
      )}

      {/* Feature Comparison Table - MOVED ABOVE Plans */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white rounded-2xl border border-slate-200 overflow-hidden"
      >
        <div className="p-4 sm:p-5 border-b border-slate-200">
          <h3 className="text-lg sm:text-xl font-bold text-slate-900">Feature Comparison</h3>
          <p className="text-xs sm:text-sm text-slate-500 mt-1">Compare all features across plans</p>
          <p className="text-[10px] sm:text-xs text-blue-600 mt-1 sm:hidden flex items-center gap-1">
            <ChevronRight className="w-3 h-3" /> Scroll horizontally to see all plans
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[480px] text-[10px] sm:text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-left py-2.5 px-2 sm:py-4 sm:px-4 text-[10px] sm:text-sm font-semibold text-slate-600 sticky left-0 bg-slate-50 z-10 min-w-[100px] sm:min-w-[140px]">Feature</th>
                {['starter', 'growth', 'professional', 'enterprise'].map(planId => {
                  const isCurrentUserPlan = normalizedCurrentPlan === planId;
                  return (
                    <th key={planId} className={`text-center py-2.5 px-1.5 sm:py-4 sm:px-3 text-[10px] sm:text-sm font-semibold min-w-[70px] sm:min-w-[90px] ${
                      isCurrentUserPlan ? 'text-blue-600 bg-blue-50' : 'text-slate-600'
                    }`}>
                      <div className="flex flex-col items-center gap-0.5">
                        <span className="capitalize text-[10px] sm:text-sm">{planId === 'professional' ? 'Pro' : planId}</span>
                        {isCurrentUserPlan && (
                          <span className="text-[7px] sm:text-[10px] px-1 py-0.5 bg-blue-600 text-white rounded-full whitespace-nowrap">Current</span>
                        )}
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {FEATURE_COMPARISON.map((category, catIdx) => (
                <React.Fragment key={catIdx}>
                  <tr className="bg-slate-50/50">
                    <td colSpan={5} className="py-2 sm:py-3 px-2 sm:px-4 text-[10px] sm:text-sm font-semibold text-slate-700 sticky left-0 bg-slate-50/50">
                      {category.category}
                    </td>
                  </tr>
                  {category.features.map((feature, featIdx) => (
                    <tr key={featIdx} className="hover:bg-slate-50">
                      <td className="py-2 sm:py-3 px-2 sm:px-4 text-[10px] sm:text-sm text-slate-700 sticky left-0 bg-white">{feature.name}</td>
                      {['starter', 'growth', 'professional', 'enterprise'].map(planId => (
                        <td key={planId} className={`text-center py-2 sm:py-3 px-1 sm:px-3 ${
                          normalizedCurrentPlan === planId ? 'bg-blue-50/50' : ''
                        }`}>
                          <FeatureValue 
                            value={feature[planId]} 
                            highlight={normalizedCurrentPlan === planId}
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </motion.div>

      {/* Choose Your Plan */}
      <div>
        <div className="mb-4 sm:mb-6">
          <h3 className="text-lg sm:text-xl font-bold text-slate-900">Choose Your Plan</h3>
          <p className="text-slate-600 text-xs sm:text-sm mt-1">All prices exclude 18% GST. Credits renew every 30 days.</p>
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
          {plans.map((plan, index) => {
            const normalizedPlanId = plan.id?.toLowerCase() === 'basic' ? 'starter' : plan.id?.toLowerCase();
            const isCurrentUserPlan = normalizedCurrentPlan === normalizedPlanId;
            const isUpgrade = getPlanOrder(plan.id) > getPlanOrder(currentPlan);
            const isDowngrade = getPlanOrder(plan.id) < getPlanOrder(currentPlan);
            
            return (
              <motion.div
                key={plan.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`relative rounded-2xl border-2 overflow-hidden flex flex-col ${
                  isCurrentUserPlan 
                    ? 'border-blue-500 bg-blue-50/30' 
                    : plan.popular 
                      ? 'border-amber-400 bg-white' 
                      : 'border-slate-200 bg-white'
                }`}
              >
                {/* Current Plan Badge */}
                {isCurrentUserPlan && (
                  <div className="absolute top-0 right-0 bg-blue-600 text-white text-xs font-semibold px-3 py-1 rounded-bl-lg z-10">
                    Current Plan
                  </div>
                )}
                
                {/* Popular Badge */}
                {plan.popular && !isCurrentUserPlan && (
                  <div className="bg-amber-400 text-amber-900 text-xs font-semibold text-center py-1.5">
                    Most Popular
                  </div>
                )}
                
                <div className={`p-4 sm:p-5 flex-1 flex flex-col ${plan.popular && !isCurrentUserPlan ? '' : isCurrentUserPlan ? 'pt-6 sm:pt-8' : ''}`}>
                  {/* Plan Header */}
                  <div className="flex items-center gap-2 sm:gap-3 mb-3">
                    <div className={`p-2 sm:p-2.5 rounded-xl ${
                      isCurrentUserPlan ? 'bg-blue-100 text-blue-600' : 'bg-slate-100 text-slate-600'
                    }`}>
                      <PlanIcon planId={plan.id} className="w-5 h-5 sm:w-6 sm:h-6" />
                    </div>
                    <div>
                      <h4 className="font-bold text-slate-900 text-base sm:text-lg">{plan.name}</h4>
                      <p className="text-[10px] sm:text-xs text-slate-500">{plan.tagline}</p>
                    </div>
                  </div>
                  
                  {/* Pricing */}
                  <div className="mb-3 sm:mb-4">
                    {plan.effective_price === 0 ? (
                      <div>
                        <span className="text-2xl sm:text-3xl font-bold text-slate-900">Free</span>
                        <p className="text-xs sm:text-sm text-slate-500">Pay as you go</p>
                      </div>
                    ) : plan.effective_price === -1 ? (
                      <div>
                        <span className="text-xl sm:text-2xl font-bold text-slate-900">Custom</span>
                        <p className="text-xs sm:text-sm text-slate-500">Contact sales</p>
                      </div>
                    ) : (
                      <div>
                        <div className="flex items-baseline gap-2">
                          <span className="text-2xl sm:text-3xl font-bold text-slate-900">
                            ₹{(plan.effective_price || 0).toLocaleString()}
                          </span>
                          {isIntroPeriod && plan.price_monthly > plan.effective_price && (
                            <span className="text-xs sm:text-sm text-slate-400 line-through">
                              ₹{plan.price_monthly.toLocaleString()}
                            </span>
                          )}
                        </div>
                        <p className="text-xs sm:text-sm text-slate-500">/month + GST</p>
                        {plan.effective_credits > 0 && (
                          <p className="text-xs sm:text-sm text-emerald-600 font-medium mt-1">
                            +{plan.effective_credits.toLocaleString()} credits included
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                  
                  {/* Key Benefits - Simplified */}
                  <div className="flex-1 mb-3 sm:mb-4">
                    <PlanBenefits plan={plan} />
                  </div>
                  
                  {/* Credit Costs Summary */}
                  <div className="bg-slate-50 rounded-lg p-2.5 sm:p-3 mb-3 sm:mb-4">
                    <div className="flex justify-between text-xs sm:text-sm">
                      <span className="text-slate-600">VTO:</span>
                      <span className="font-semibold text-slate-800">{plan.tryon_cost} credits</span>
                    </div>
                    <div className="flex justify-between text-xs sm:text-sm">
                      <span className="text-slate-600">Moments:</span>
                      <span className="font-semibold text-slate-800">{plan.moments_cost} credits</span>
                    </div>
                  </div>
                  
                  {/* CTA Button */}
                  <button
                    onClick={() => !isCurrentUserPlan && !isDowngrade && openPaymentModal(plan)}
                    disabled={isCurrentUserPlan || isDowngrade || upgrading === plan.id}
                    className={`w-full py-2.5 sm:py-3 rounded-xl font-semibold text-xs sm:text-sm transition-all ${
                      isCurrentUserPlan
                        ? 'bg-blue-100 text-blue-600 cursor-default'
                        : isDowngrade
                          ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                          : plan.id === 'enterprise'
                            ? 'bg-slate-900 text-white hover:bg-slate-800'
                            : 'bg-blue-600 text-white hover:bg-blue-700'
                    }`}
                    data-testid={`plan-${plan.id}-btn`}
                  >
                    {isCurrentUserPlan ? (
                      <span className="flex items-center justify-center gap-2">
                        <Check className="w-4 h-4" /> Your Current Plan
                      </span>
                    ) : isDowngrade ? (
                      'Contact Support to Downgrade'
                    ) : upgrading === plan.id ? (
                      <span className="flex items-center justify-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin" /> Processing...
                      </span>
                    ) : (
                      <span className="flex items-center justify-center gap-2">
                        {plan.id === 'enterprise' ? 'Talk to Sales' : `Upgrade to ${plan.name}`}
                        <ArrowRight className="w-4 h-4" />
                      </span>
                    )}
                  </button>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* FAQ Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="bg-slate-50 rounded-2xl p-4 sm:p-6"
      >
        <h3 className="text-base sm:text-lg font-bold text-slate-900 mb-3 sm:mb-4">Frequently Asked Questions</h3>
        <div className="grid sm:grid-cols-2 gap-3 sm:gap-4">
          <FAQItem 
            question="Can I change my plan later?"
            answer="Yes! You can upgrade your plan at any time. Upgrades take effect immediately. Contact support if you need to downgrade."
          />
          <FAQItem 
            question="What happens to unused usage balance?"
            answer="Monthly included usage balance refreshes each billing cycle and does not roll over. However, any additional usage balance you purchase (top-up) is valid for up to 12 months."
          />
          <FAQItem 
            question="Is there a free trial?"
            answer="The Starter plan is free forever with pay-as-you-go pricing. No credit card required to get started."
          />
          <FAQItem 
            question="How does billing work?"
            answer="Subscriptions are billed monthly. You'll receive an invoice with GST details after each payment."
          />
        </div>
      </motion.div>

      {/* Help Section */}
      <div className="bg-gradient-to-r from-slate-800 to-slate-900 rounded-2xl p-4 sm:p-6 text-center">
        <h3 className="font-bold text-white text-base sm:text-lg mb-2">Need Help Choosing?</h3>
        <p className="text-slate-300 text-xs sm:text-sm mb-3 sm:mb-4">
          Our team can help you find the perfect plan for your business needs.
        </p>
        <a 
          href="/contact" 
          className="inline-flex items-center gap-2 px-4 sm:px-5 py-2 sm:py-2.5 bg-white text-slate-900 rounded-lg text-xs sm:text-sm font-semibold hover:bg-slate-100 transition-colors"
        >
          <Headphones className="w-4 h-4" />
          Talk to Sales
        </a>
      </div>

      {/* Payment Modal */}
      <AnimatePresence>
        {showPaymentModal && selectedPlanForPayment && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-2xl max-w-md w-full overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {paymentStep === 'confirm' && (
                <>
                  <div className="p-6 border-b border-slate-100">
                    <div className="flex items-center justify-between">
                      <h2 className="text-xl font-bold text-slate-900">Confirm Upgrade</h2>
                      <button 
                        onClick={() => setShowPaymentModal(false)}
                        className="p-2 hover:bg-slate-100 rounded-lg"
                      >
                        <X className="w-5 h-5 text-slate-500" />
                      </button>
                    </div>
                  </div>
                  
                  <div className="p-6">
                    {/* Plan Summary */}
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 mb-6">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="p-2 bg-blue-100 rounded-lg">
                          <PlanIcon planId={selectedPlanForPayment.id} className="w-6 h-6 text-blue-600" />
                        </div>
                        <div>
                          <h3 className="font-bold text-slate-900">{selectedPlanForPayment.name} Plan</h3>
                          <p className="text-sm text-slate-600">Monthly Subscription</p>
                        </div>
                      </div>
                      
                      {/* What you get */}
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center gap-2 text-slate-700">
                          <Check className="w-4 h-4 text-emerald-500" />
                          <span>{selectedPlanForPayment.effective_credits?.toLocaleString() || 0} credits/month</span>
                        </div>
                        <div className="flex items-center gap-2 text-slate-700">
                          <Check className="w-4 h-4 text-emerald-500" />
                          <span>VTO: ₹{selectedPlanForPayment.tryon_cost}/call (save ₹{24 - selectedPlanForPayment.tryon_cost} vs Starter)</span>
                        </div>
                        <div className="flex items-center gap-2 text-slate-700">
                          <Check className="w-4 h-4 text-emerald-500" />
                          <span>Moments: ₹{selectedPlanForPayment.moments_cost}/call (save ₹{10 - selectedPlanForPayment.moments_cost} vs Starter)</span>
                        </div>
                        <div className="flex items-center gap-2 text-slate-700">
                          <Check className="w-4 h-4 text-emerald-500" />
                          <span>Up to {selectedPlanForPayment.max_skus} SKUs</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Payment Breakdown */}
                    <div className="bg-slate-50 rounded-xl p-4 mb-6">
                      <h4 className="font-semibold text-slate-800 mb-3">Payment Summary</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-slate-600">{selectedPlanForPayment.name} Plan (Monthly)</span>
                          <span className="font-medium">₹{selectedPlanForPayment.effective_price?.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-600">GST (18%)</span>
                          <span className="font-medium">₹{calculateGST(selectedPlanForPayment.effective_price).toLocaleString()}</span>
                        </div>
                        <div className="border-t border-slate-200 pt-2 mt-2 flex justify-between">
                          <span className="font-semibold text-slate-800">Total Amount</span>
                          <span className="font-bold text-lg text-blue-600">
                            ₹{(selectedPlanForPayment.effective_price + calculateGST(selectedPlanForPayment.effective_price)).toLocaleString()}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Info Note */}
                    <div className="flex items-start gap-2 text-xs text-slate-500 mb-6">
                      <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
                      <span>Your new plan activates immediately after payment. Included usage balance will be added to your account.</span>
                    </div>
                    
                    {/* Buttons */}
                    <div className="flex gap-3">
                      <button
                        onClick={() => setShowPaymentModal(false)}
                        className="flex-1 py-3 border border-slate-200 text-slate-700 rounded-xl font-medium hover:bg-slate-50"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleUpgrade}
                        className="flex-1 py-3 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 flex items-center justify-center gap-2"
                      >
                        <CreditCard className="w-4 h-4" />
                        Pay ₹{(selectedPlanForPayment.effective_price + calculateGST(selectedPlanForPayment.effective_price)).toLocaleString()}
                      </button>
                    </div>
                    
                    <p className="text-center text-xs text-slate-400 mt-4">
                      Secured by Razorpay • GST Invoice Included
                    </p>
                  </div>
                </>
              )}
              
              {paymentStep === 'processing' && (
                <div className="p-8 text-center">
                  <Loader2 className="w-16 h-16 mx-auto mb-6 text-blue-600 animate-spin" />
                  <h2 className="text-xl font-bold text-slate-900 mb-2">Processing Payment...</h2>
                  <p className="text-slate-600">Please wait while we verify your payment</p>
                </div>
              )}
              
              {paymentStep === 'success' && (
                <div className="p-8 text-center">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring", damping: 10 }}
                    className="w-20 h-20 mx-auto mb-6 rounded-full bg-emerald-500 flex items-center justify-center"
                  >
                    <Check className="w-10 h-10 text-white" />
                  </motion.div>
                  <h2 className="text-xl font-bold text-slate-900 mb-2">Welcome to {selectedPlanForPayment.name}!</h2>
                  <p className="text-slate-600 mb-4">Your subscription is now active</p>
                  <div className="text-3xl font-bold text-emerald-600 mb-2">
                    +{selectedPlanForPayment.effective_credits?.toLocaleString() || 0} Credits
                  </div>
                  <p className="text-sm text-slate-500">Added to your account</p>
                </div>
              )}
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Helper Components
const FeatureBadge = ({ enabled, label, icon }) => (
  <div className={`flex items-center gap-1 sm:gap-1.5 px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-full text-[10px] sm:text-xs font-medium ${
    enabled ? 'bg-white/20 text-white' : 'bg-white/10 text-white/50'
  }`}>
    {enabled ? icon : <Lock className="w-3 h-3" />}
    {label}
  </div>
);

const FAQItem = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div className="bg-white rounded-xl p-4 border border-slate-100">
      <button 
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between text-left"
      >
        <span className="font-medium text-slate-800 text-sm">{question}</span>
        {isOpen ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <p className="text-sm text-slate-600 mt-2 pt-2 border-t border-slate-100">{answer}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Subscription;
